import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; 
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, deleteDoc, getDocs, writeBatch } from 'firebase/firestore';
import { startOfWeek, addDays, format } from 'date-fns'; 
import { useNavigate } from 'react-router-dom';
import { User } from 'lucide-react'; // Added for CRM button icon

// --- HELPER: Get Week Days ---
const getWeekDays = (startDate) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
        days.push(addDays(startDate, i));
    }
    return days;
};

// --- SUB-COMPONENT: BULK ACTION MODAL ---
const BulkActionModal = ({ isOpen, onClose, onSave, currentWeekStart }) => {
    if (!isOpen) return null;

    const [selectedDays, setSelectedDays] = useState({
        Mon: true, Tue: true, Wed: true, Thu: true, Fri: true, Sat: false, Sun: false
    });
    const [timeStart, setTimeStart] = useState("09:00");
    const [timeEnd, setTimeEnd] = useState("17:00");
    const [config, setConfig] = useState({ type: '1:1', topic: 'General' });
    const [mode, setMode] = useState('create'); 

    const timeOptions = Array.from({ length: 18 }, (_, i) => `${(i + 6).toString().padStart(2, '0')}:00`);
    const weekDays = getWeekDays(currentWeekStart);

    const toggleDay = (dayName) => setSelectedDays(prev => ({ ...prev, [dayName]: !prev[dayName] }));

    const handleSave = () => {
        const targetDates = weekDays.filter(d => selectedDays[format(d, 'EEE')]);
        onSave({ targetDates, timeStart, timeEnd, config, mode });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-2xl w-[500px]">
                <h3 className="text-xl font-bold mb-4 text-indigo-900 border-b pb-2">⚡ Bulk Scheduler</h3>
                <div className="flex gap-4 mb-6">
                    <button onClick={() => setMode('create')} className={`flex-1 py-2 rounded font-bold ${mode === 'create' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}>Create / Update Slots</button>
                    <button onClick={() => setMode('delete')} className={`flex-1 py-2 rounded font-bold ${mode === 'delete' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-500'}`}>Delete Slots</button>
                </div>
                <div className="mb-4">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Which Days?</label>
                    <div className="flex justify-between gap-1">
                        {Object.keys(selectedDays).map(day => (
                            <button key={day} onClick={() => toggleDay(day)} className={`w-10 h-10 rounded-full text-sm font-bold transition-colors ${selectedDays[day] ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500' : 'bg-gray-50 text-gray-400 border border-gray-200'}`}>{day.charAt(0)}</button>
                        ))}
                    </div>
                </div>
                <div className="flex gap-4 mb-4">
                    <div className="flex-1">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">From</label>
                        <select value={timeStart} onChange={e => setTimeStart(e.target.value)} className="w-full border p-2 rounded">{timeOptions.map(t => <option key={t} value={t}>{t}</option>)}</select>
                    </div>
                    <div className="flex-1">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Until (Inclusive)</label>
                        <select value={timeEnd} onChange={e => setTimeEnd(e.target.value)} className="w-full border p-2 rounded">{timeOptions.map(t => <option key={t} value={t}>{t}</option>)}</select>
                    </div>
                </div>
                {mode === 'create' && (
                    <div className="grid grid-cols-2 gap-4 mb-6 bg-gray-50 p-3 rounded">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Type</label>
                            <select value={config.type} onChange={e => setConfig({...config, type: e.target.value})} className="w-full border p-2 rounded text-sm">
                                <option value="1:1">1:1 Coaching</option>
                                <option value="Group">Group Session</option>
                                <option value="Consultation">Free Consultation</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Topic</label>
                            <select value={config.topic} onChange={e => setConfig({...config, topic: e.target.value})} className="w-full border p-2 rounded text-sm">
                                <option value="General">📘 General</option>
                                <option value="Aviation">✈️ Aviation</option>
                                <option value="Medical">🏥 Medical</option>
                                <option value="Legal">⚖️ Legal</option>
                                <option value="Tech">💻 Tech</option>
                            </select>
                        </div>
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-4 border-t">
                    <button onClick={onClose} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded">Cancel</button>
                    <button onClick={handleSave} className={`px-6 py-2 text-white rounded font-bold shadow ${mode === 'create' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-red-500 hover:bg-red-600'}`}>
                        {mode === 'create' ? 'Apply Schedule' : 'Bulk Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- SUB-COMPONENT: SLOT MANAGER MODAL ---
const SlotManagerModal = ({ isOpen, onClose, onSave, onDelete, initialData, studentsList, isTeacher, isAdmin }) => {
    const [formData, setFormData] = useState({
        type: '1:1', topic: 'General', studentId: '', notes: ''
    });
    const navigate = useNavigate();

    useEffect(() => {
        if (initialData) {
            setFormData({
                type: initialData.type || '1:1',
                topic: initialData.topic || 'General',
                studentId: initialData.studentId || '',
                notes: initialData.notes || ''
            });
        } else {
            setFormData({ type: '1:1', topic: 'General', studentId: '', notes: '' });
        }
    }, [initialData, isOpen]);

    if (!isOpen) return null;
    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-96 z-50 relative">
                <button onClick={onClose} className="absolute top-2 right-3 text-gray-400 text-xl">&times;</button>
                <h3 className="text-lg font-bold mb-4 border-b pb-2 text-indigo-900">{initialData ? 'Edit Slot' : 'New Slot'}</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Class Type</label>
                        <select name="type" value={formData.type} onChange={handleChange} className="w-full border p-2 rounded">
                            <option value="1:1">1:1 Coaching</option>
                            <option value="Group">Group Session</option>
                            <option value="Consultation">Free Consultation</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Topic</label>
                        <select name="topic" value={formData.topic} onChange={handleChange} className="w-full border p-2 rounded">
                            <option value="General">📘 General English</option>
                            <option value="Aviation">✈️ Aviation</option>
                            <option value="Medical">🏥 Medical</option>
                            <option value="Legal">⚖️ Legal</option>
                            <option value="Tech">💻 IT & Tech</option>
                        </select>
                    </div>
                    {(isTeacher || isAdmin) && (
                        <div className="bg-indigo-50 p-2 rounded border border-indigo-100">
                            <label className="block text-xs font-bold text-indigo-800 uppercase mb-1">Assign Student</label>
                            <select name="studentId" value={formData.studentId} onChange={handleChange} className="w-full border p-2 rounded bg-white">
                                <option value="">-- Open (Anyone) --</option>
                                {studentsList.map(s => <option key={s.id} value={s.id}>{s.displayName || s.email}</option>)}
                            </select>
                            {/* CRM LINK - Only shows when a student is selected */}
                            {formData.studentId && (
                                <button 
                                    type="button"
                                    onClick={() => navigate(`/crm?studentId=${formData.studentId}`)} 
                                    className="mt-2 text-[10px] text-indigo-600 font-bold underline uppercase flex items-center gap-1"
                                >
                                    <User size={12} /> View Student CRM Profile
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex justify-between mt-6 pt-4 border-t">
                    {initialData ? <button onClick={onDelete} className="text-red-600 font-bold text-sm">Delete</button> : <div/>}
                    <div className="flex gap-2">
                        <button onClick={onClose} className="text-gray-500 text-sm">Cancel</button>
                        <button onClick={() => onSave(formData)} className="bg-indigo-600 text-white px-4 py-2 rounded font-bold text-sm shadow">Save</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- MAIN SCHEDULER COMPONENT ---
const Scheduler = ({ user, role, onBookingComplete }) => {
    const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 })); 
    const [appointments, setAppointments] = useState([]);
    
    // Modals
    const [isSlotModalOpen, setIsSlotModalOpen] = useState(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null); 
    const [activeSlotInfo, setActiveSlotInfo] = useState({ date: null, time: null });

    // Data Lists & Selection
    const [adminSelectedTeacher, setAdminSelectedTeacher] = useState('');
    const [studentViewTeacher, setStudentViewTeacher] = useState(''); 
    const [teachersList, setTeachersList] = useState([]);
    const [studentsList, setStudentsList] = useState([]);

    const weekDays = getWeekDays(currentWeekStart);
    const timeSlots = Array.from({ length: 18 }, (_, i) => `${(i + 6).toString().padStart(2, '0')}:00`);

    // 1. FETCH DATA
    useEffect(() => {
        let q;
        const targetTeacherId = (role === 'admin' && adminSelectedTeacher) ? adminSelectedTeacher : (role === 'teacher' ? user.uid : null);

        if (targetTeacherId) {
             q = query(collection(db, 'appointments'), where('teacherId', '==', targetTeacherId));
        } else if (role === 'guest') {
             q = query(collection(db, 'appointments'), where('type', '==', 'Consultation'));
        } else {
             q = query(collection(db, 'appointments'));
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const apps = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAppointments(apps);
        });

        fetchUsersList();
        return () => unsubscribe();
    }, [user, role, adminSelectedTeacher]);

    const fetchUsersList = async () => {
        const tSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'teacher')));
        setTeachersList(tSnap.docs.map(d => ({id: d.id, ...d.data()})));

        if (role === 'admin' || role === 'teacher') {
            const sSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'student')));
            setStudentsList(sSnap.docs.map(d => ({id: d.id, ...d.data()})));
        }
    };

    // 2. BULK SAVE HANDLER
    const handleBulkSave = async ({ targetDates, timeStart, timeEnd, config, mode }) => {
        const batch = writeBatch(db);
        const teacherId = role === 'admin' ? adminSelectedTeacher : user.uid;
        const teacherName = user.displayName || "Teacher";

        const times = [];
        let currentH = parseInt(timeStart.split(':')[0]);
        const endH = parseInt(timeEnd.split(':')[0]);
        while (currentH <= endH) {
            times.push(`${currentH.toString().padStart(2, '0')}:00`);
            currentH++;
        }

        targetDates.forEach(dateObj => {
            const dateStr = format(dateObj, 'yyyy-MM-dd');
            times.forEach(time => {
                const existing = appointments.find(a => a.date === dateStr && a.time === time && a.teacherId === teacherId);
                if (existing && existing.status === 'booked') return;

                if (mode === 'delete') {
                    if (existing) batch.delete(doc(db, 'appointments', existing.id));
                } else {
                    const slotData = {
                        teacherId, teacherName,
                        date: dateStr, time,
                        status: 'available',
                        type: config.type,
                        topic: config.topic,
                        studentId: null, studentName: null
                    };
                    const ref = existing ? doc(db, 'appointments', existing.id) : doc(collection(db, 'appointments'));
                    existing ? batch.update(ref, slotData) : batch.set(ref, slotData);
                }
            });
        });

        try {
            await batch.commit();
            setIsBulkModalOpen(false);
            alert(`Batch operation complete!`);
        } catch (err) {
            console.error(err);
            alert("Error saving batch.");
        }
    };

    // 3. SINGLE CELL CLICK
    const handleCellClick = async (dateStr, time, existingApp) => {
      if (role === 'guest' && existingApp?.status === 'available') {
          if (window.confirm(`Confirm free consultation on ${dateStr} at ${time}?`)) {
               await updateDoc(doc(db, 'appointments', existingApp.id), {
                  status: 'booked',
                  studentId: 'GUEST_LEAD',
                  studentName: user.displayName,
                  studentEmail: user.email,
                  bookedAt: new Date()
              });
              alert("Booking Confirmed!");
              if(onBookingComplete) onBookingComplete();
          }
          return;
      }

      if (role === 'teacher' || role === 'admin') {
          setActiveSlotInfo({ date: dateStr, time: time });
          setModalData(existingApp || null);
          setIsSlotModalOpen(true);
          return;
      }
      
      if (role === 'student' && existingApp?.status === 'available') {
           if (window.confirm(`Book ${existingApp.topic} class with ${existingApp.teacherName || 'Teacher'} on ${dateStr} at ${time}?`)) {
              await updateDoc(doc(db, 'appointments', existingApp.id), {
                  status: 'booked',
                  studentId: user.uid,
                  studentName: user.displayName || 'Student'
              });
          }
      }
    };

    const handleSaveSlot = async (formData) => {
        const studentName = formData.studentId ? studentsList.find(s => s.id === formData.studentId)?.displayName : null;
        const status = formData.studentId ? 'booked' : 'available';

        try {
            if (modalData) {
                await updateDoc(doc(db, 'appointments', modalData.id), { ...formData, status, studentName: studentName || null });
            } else {
                await addDoc(collection(db, 'appointments'), {
                    teacherId: role === 'admin' ? adminSelectedTeacher : user.uid,
                    teacherName: user.displayName || "Teacher",
                    date: activeSlotInfo.date,
                    time: activeSlotInfo.time,
                    status: status,
                    ...formData,
                    studentName: studentName || null
                });
            }
            setIsSlotModalOpen(false);
        } catch (err) { console.error(err); }
    };

    const handleDeleteSlot = async () => {
        if (modalData && window.confirm("Delete this slot?")) {
            await deleteDoc(doc(db, 'appointments', modalData.id));
            setIsSlotModalOpen(false);
        }
    };

    const getTopicIcon = (topic) => {
        const icons = { 'Aviation': '✈️', 'Medical': '🏥', 'Legal': '⚖️', 'Banking': '💰', 'Tech': '💻', 'General': '📘' };
        return icons[topic] || '📘';
    };

    return (
        <div className="bg-white rounded shadow flex flex-col h-full relative">
            {/* Header */}
            <div className="p-4 border-b flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50 rounded-t">
                <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold flex items-center gap-2 text-indigo-800">
                        📅 {format(currentWeekStart, 'MMM d')} - {format(addDays(currentWeekStart, 6), 'MMM d')}
                    </h2>
                    {(role === 'teacher' || (role === 'admin' && adminSelectedTeacher)) && (
                        <button onClick={() => setIsBulkModalOpen(true)} className="flex items-center gap-2 bg-indigo-600 text-white px-3 py-1 rounded text-sm font-bold shadow hover:bg-indigo-700 transition-colors">
                            ⚡ Bulk Edit
                        </button>
                    )}
                </div>
                
                {role === 'student' && (
                     <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-500 uppercase">Instructor:</span>
                        <select 
                            value={studentViewTeacher} 
                            onChange={(e) => setStudentViewTeacher(e.target.value)}
                            className="border border-indigo-300 rounded px-2 py-1 text-sm bg-indigo-50 text-indigo-900 font-medium"
                        >
                            <option value="">All Instructors</option>
                            {teachersList.map(t => (
                                <option key={t.id} value={t.id}>{t.displayName || 'Teacher'}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="flex gap-2">
                    <button onClick={() => setCurrentWeekStart(addDays(currentWeekStart, -7))} className="px-3 py-1 bg-white border rounded hover:bg-gray-100 shadow-sm">Previous</button>
                    <button onClick={() => setCurrentWeekStart(new Date())} className="px-3 py-1 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded font-bold shadow-sm">Today</button>
                    <button onClick={() => setCurrentWeekStart(addDays(currentWeekStart, 7))} className="px-3 py-1 bg-white border rounded hover:bg-gray-100 shadow-sm">Next</button>
                </div>
            </div>

            {role === 'admin' && (
                <div className="bg-yellow-50 p-4 border-b border-yellow-200 flex flex-wrap gap-4 items-center justify-center">
                    <span className="font-bold text-yellow-800 uppercase text-xs tracking-wide">Admin View:</span>
                    <select onChange={(e) => setAdminSelectedTeacher(e.target.value)} className="border p-2 rounded text-sm shadow-sm">
                        <option value="">-- Select Teacher to Manage --</option>
                        {teachersList.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)}
                    </select>
                </div>
            )}

            <div className="flex-1 overflow-auto relative bg-white">
                <div className="min-w-[800px]"> 
                    <div className="grid grid-cols-8 border-b sticky top-0 bg-white z-10 shadow-sm">
                        <div className="p-3 text-center text-gray-400 font-bold border-r bg-gray-50 text-sm">Time</div>
                        {weekDays.map(day => (
                            <div key={day.toString()} className={`p-3 text-center border-r font-semibold ${format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'}`}>
                                <span className="block text-sm">{format(day, 'EEE')}</span>
                                <span className="text-xs text-gray-400 font-normal">{format(day, 'MMM d')}</span>
                            </div>
                        ))}
                    </div>

                    {timeSlots.map(time => (
                        <div key={time} className="grid grid-cols-8 border-b hover:bg-gray-50 transition-colors h-20">
                            <div className="p-2 text-center text-xs text-gray-400 font-bold border-r flex items-center justify-center bg-gray-50">{time}</div>
                            {weekDays.map(day => {
                                const dateStr = format(day, 'yyyy-MM-dd');
                                const app = appointments.find(a => {
                                    const matchTime = a.date === dateStr && a.time === time;
                                    if (role === 'student' && studentViewTeacher) {
                                        return matchTime && a.teacherId === studentViewTeacher;
                                    }
                                    return matchTime;
                                });

                                let cellContent = null;
                                let cellClass = "cursor-pointer hover:bg-gray-100"; 

                                if (app) {
                                    if (app.status === 'available') {
                                        cellClass = "bg-green-50 hover:bg-green-100 border-green-200 cursor-pointer border-l-4 border-green-400 p-1";
                                        cellContent = (
                                            <div className="h-full flex flex-col justify-center">
                                                <div className="font-bold text-green-700 text-xs flex items-center gap-1">{getTopicIcon(app.topic)} OPEN</div>
                                                <div className="text-[10px] text-gray-500">{app.type}</div>
                                                {role === 'student' && !studentViewTeacher && (
                                                    <div className="text-[9px] text-indigo-400 font-bold">{app.teacherName?.split(' ')[0]}</div>
                                                )}
                                            </div>
                                        );
                                    } else if (app.status === 'booked') {
                                        const isMe = app.studentId === user.uid || app.teacherId === user.uid || role === 'admin';
                                        if (isMe) {
                                            cellClass = "bg-indigo-50 border-indigo-200 cursor-pointer border-l-4 border-indigo-500 p-1";
                                            cellContent = (
                                                <div className="h-full flex flex-col justify-center">
                                                    <div className="font-bold text-indigo-700 text-xs flex items-center gap-1">{getTopicIcon(app.topic)} BOOKED</div>
                                                    <div className="text-[10px] text-indigo-600 truncate font-medium">w/ {role === 'teacher' ? (app.studentName || 'Student') : (app.teacherName || 'Teacher')}</div>
                                                </div>
                                            );
                                        } else {
                                            cellClass = "bg-gray-100 opacity-60 cursor-not-allowed p-1";
                                            cellContent = <div className="h-full flex items-center justify-center text-[10px] text-gray-400 font-bold tracking-widest">BUSY</div>;
                                        }
                                    }
                                } else if (role === 'teacher' || role === 'admin') {
                                    cellContent = <div className="hidden group-hover:flex items-center justify-center h-full text-indigo-300 text-3xl font-light">+</div>;
                                }

                                return (
                                    <div key={`${dateStr}-${time}`} className={`border-r transition-all relative group ${cellClass}`} onClick={() => handleCellClick(dateStr, time, app)}>
                                        {cellContent}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            <SlotManagerModal 
                isOpen={isSlotModalOpen} 
                onClose={() => setIsSlotModalOpen(false)}
                onSave={handleSaveSlot}
                onDelete={handleDeleteSlot}
                initialData={modalData}
                studentsList={studentsList}
                isTeacher={role === 'teacher'}
                isAdmin={role === 'admin'}
            />

            <BulkActionModal 
                isOpen={isBulkModalOpen}
                onClose={() => setIsBulkModalOpen(false)}
                onSave={handleBulkSave}
                currentWeekStart={currentWeekStart}
            />
        </div>
    );
};

export default Scheduler;